/**
 * Cookie Consent Management Platform - React Context
 * 全局管理同意状态，控制 SDK 加载
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  ConsentChoices,
  DEFAULT_CONSENT,
  ACCEPT_ALL_CONSENT,
  getLocalConsent,
  setLocalConsent,
  getLocalPolicyVersion,
  setLocalPolicyVersion,
  shouldShowBanner,
} from '../util/cmpStorage';
import { fetchConsentConfig, postConsent } from '../util/cmpApi';

interface CmpContextValue {
  // 当前同意状态
  consent: ConsentChoices;
  // 当前策略版本
  policyVersion: string;
  // 是否已初始化
  initialized: boolean;
  // 是否显示 Banner
  showBanner: boolean;
  // 是否显示设置弹窗
  showSettings: boolean;
  // 操作
  acceptAll: () => void;
  acceptEssentials: () => void;
  saveSettings: (choices: ConsentChoices) => void;
  openSettings: () => void;
  closeSettings: () => void;
  closeBanner: () => void;
}

const CmpContext = createContext<CmpContextValue | null>(null);

export function useCmp() {
  const ctx = useContext(CmpContext);
  if (!ctx) {
    throw new Error('useCmp must be used within CmpProvider');
  }
  return ctx;
}

// 安全版本，用于不确定是否在 Provider 内的场景
export function useCmpSafe(): CmpContextValue | null {
  return useContext(CmpContext);
}

interface CmpProviderProps {
  children: React.ReactNode;
}

export function CmpProvider({ children }: CmpProviderProps) {
  const [consent, setConsent] = useState<ConsentChoices>(DEFAULT_CONSENT);
  const [policyVersion, setPolicyVersion] = useState<string>('');
  const [initialized, setInitialized] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // 初始化：读取本地存储 + 拉取配置
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // 1. 读取本地同意
      const localConsent = getLocalConsent();
      if (localConsent) {
        setConsent(localConsent);
      }

      // 2. 拉取服务端配置
      const config = await fetchConsentConfig();
      if (cancelled) return;

      const serverVersion = config.policyVersion || 'default';
      setPolicyVersion(serverVersion);

      // 3. 判断是否需要展示 Banner
      // 首次访问（无本地同意）或版本不一致时，展示底部悬浮 Banner
      if (!localConsent) {
        // 首次访问，必须展示 Banner
        setShowBanner(true);
      } else if (shouldShowBanner(serverVersion)) {
        // 有本地同意但版本不一致，需要重新确认
        setShowBanner(true);
      } else {
        // 有本地同意且版本一致，直接使用
        setConsent(localConsent);
      }

      setInitialized(true);

      // 4. 检查 URL 参数，如果带 ?open-cookie-settings 则自动打开设置弹窗
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('open-cookie-settings')) {
          setShowSettings(true);
          // 清除 URL 参数，避免刷新后重复打开
          const newUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, '', newUrl);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // 保存同意并上报
  const persistConsent = useCallback(
    async (choices: ConsentChoices) => {
      // 强制 necessary = true
      const finalChoices: ConsentChoices = { ...choices, necessary: true };
      setConsent(finalChoices);
      setLocalConsent(finalChoices);
      if (policyVersion) {
        setLocalPolicyVersion(policyVersion);
      }
      setShowBanner(false);
      setShowSettings(false);

      // 异步上报，不阻塞 UI
      if (policyVersion) {
        postConsent(policyVersion, finalChoices);
      }
    },
    [policyVersion]
  );

  const acceptAll = useCallback(() => {
    persistConsent(ACCEPT_ALL_CONSENT);
  }, [persistConsent]);

  const acceptEssentials = useCallback(() => {
    persistConsent(DEFAULT_CONSENT);
  }, [persistConsent]);

  const saveSettings = useCallback(
    (choices: ConsentChoices) => {
      persistConsent(choices);
    },
    [persistConsent]
  );

  const openSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  const closeBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  const value = useMemo<CmpContextValue>(
    () => ({
      consent,
      policyVersion,
      initialized,
      showBanner,
      showSettings,
      acceptAll,
      acceptEssentials,
      saveSettings,
      openSettings,
      closeSettings,
      closeBanner,
    }),
    [
      consent,
      policyVersion,
      initialized,
      showBanner,
      showSettings,
      acceptAll,
      acceptEssentials,
      saveSettings,
      openSettings,
      closeSettings,
      closeBanner,
    ]
  );

  return <CmpContext.Provider value={value}>{children}</CmpContext.Provider>;
}

